import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { QuestionJurisdictionDropDownField, QuestionYesNoField, QuestionNumberField, QuestionTextField } from 'components/Questionnaires/fields';
import { requiredValidator, rangeValidator, validateQuestion } from 'components/Questionnaires/validators';

export const QuestionTreatyClaimBlock = ({ block, preQualAnswers }) => {
  const dispatch = useDispatch();
  const [answer, setAnswer] = useState(block?.answers ?? {});

  const [claimed, setClaimed] = useState({
    id: 'claimed',
    label: 'questionnaire.treatyClaim.claimed.label',
    component: QuestionYesNoField,
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.treatyClaim.claimed.errorMessage' }
      ]
    },
    answer: answer?.claimed
  });
  const [country, setCountry] = useState({
    id: 'country',
    label: 'questionnaire.treatyClaim.country.label',
    info: {
      instructions: 'questionnaire.treatyClaim.country.info'
    },
    component: QuestionJurisdictionDropDownField,
    componentProps: {
      treatiesOnly: true
    },
    visible: (bAnswers, qAnswers, pAnswers) => bAnswers?.claimed === 'Yes',
    answer: answer?.country,
  });
  const [specialRatesClaimed, setSpecialRatesClaimed] = useState({
    id: 'specialRatesClaimed',
    label: 'questionnaire.treatyClaim.specialRatesClaimed.label',
    component: QuestionYesNoField,
    visible: (bAnswers, qAnswers, pAnswers) => bAnswers?.claimed === 'Yes' && bAnswers?.country,
    info: {
      instructions: 'questionnaire.treatyClaim.specialRatesClaimed.info'
    },
    answer: answer?.specialRatesClaimed
  });
  const [whdRate, setWhdRate] = useState({
    id: 'whdRate',
    label: 'questionnaire.treatyClaim.whdRate.label',
    component: QuestionNumberField,
    componentProps: {
      min: 0,
      max: 100
    },
    visible: (bAnswers, qAnswers, pAnswers) => bAnswers?.claimed === 'Yes' && bAnswers?.specialRatesClaimed === 'Yes',
    info: {
      instructions: 'questionnaire.treatyClaim.whdRate.info'
    },
    answer: answer?.whdRate
  });
  const [incomeType, setIncomeType] = useState({
    id: 'incomeType',
    label: 'questionnaire.treatyClaim.incomeType.label',
    component: QuestionTextField,
    visible: (bAnswers, qAnswers, pAnswers) => bAnswers?.claimed === 'Yes' && bAnswers?.specialRatesClaimed === 'Yes',
    info: {
      instructions: 'questionnaire.treatyClaim.incomeType.info'
    },
    answer: answer?.incomeType
  });
  const [articleParagraph, setArticleParagraph] = useState({
    id: 'articleParagraph',
    label: 'questionnaire.treatyClaim.articleParagraph.label',
    component: QuestionTextField,
    visible: (bAnswers, qAnswers, pAnswers) => bAnswers?.claimed === 'Yes' && bAnswers?.specialRatesClaimed === 'Yes',
    info: {
      instructions: 'questionnaire.treatyClaim.articleParagraph.info'
    },
    answer: answer?.articleParagraph
  });
  const [explanation, setExplanation] = useState({
    id: 'explanation',
    label: 'questionnaire.treatyClaim.explanation.label',
    component: QuestionTextField,
    visible: (bAnswers, qAnswers, pAnswers) => bAnswers?.claimed === 'Yes' && bAnswers?.specialRatesClaimed === 'Yes',
    info: {
      instructions: 'questionnaire.treatyClaim.explanation.info'
    },
    answer: answer?.explanation
  });

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const isValid = () => {
    let valid = false;
    if (claimed.answer === undefined) {
      return undefined;
    }

    if (claimed.answer === 'No') {
      valid = true;
    } else if (claimed.answer === 'Yes') {
      if (country.answer === undefined) {
        return undefined;
      }
      if (country.isValid === true) {
        if (!specialRatesClaimed.answer) {
          return undefined;
        }
        if (specialRatesClaimed.answer === 'Yes') {
          if (whdRate.isValid === undefined || incomeType.isValid === undefined || articleParagraph.isValid === undefined || explanation.isValid === undefined) {
            return undefined;
          }
          return whdRate.isValid && incomeType.isValid && articleParagraph.isValid && explanation.isValid;
        }
        if (specialRatesClaimed.answer === 'No') {
          valid = true;
        }
      }
    }
    return valid;
  };

  const getRequiredValidator = errorMessage => ({ validation: {
    conditions: [
      { condition: requiredValidator, message: errorMessage }
    ]
  } });

  const getWhdRateValidator = () => ({ validation: {
    conditions: [
      { condition: requiredValidator, message: 'questionnaire.treatyClaim.whdRate.errorMessage' },
      { condition: rangeValidator, message: 'questionnaire.treatyClaim.whdRate.rangeErrorMessage' }
    ]
  } });

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'claimed': {
        const newClaimed = { ...q, answer: value };
        validateQuestion(newClaimed);
        setClaimed(newClaimed);

        const countryValidation = value === 'Yes' ? getRequiredValidator('questionnaire.treatyClaim.country.errorMessage') : undefined;
        const countryChanges = value === 'No' ? { answer: undefined, isValid: undefined, errorMessages: undefined } : {};
        const newCountry = { ...country, ...countryValidation, ...countryChanges };
        validateQuestion(newCountry);
        setCountry(newCountry);
        setAnswer(value === 'Yes' ? { ...answer, claimed: value } : { claimed: value });
        break;
      }
      case 'country': {
        const newCountry = { ...q, answer: value };
        validateQuestion(newCountry);
        setCountry(newCountry);

        const hasValue = value !== undefined && value !== '';
        const srValidation = hasValue ? getRequiredValidator('questionnaire.treatyClaim.specialRatesClaimed.errorMessage') : undefined;
        setSpecialRatesClaimed({ ...specialRatesClaimed, ...srValidation });
        const changes = { country: value, specialRatesClaimed: hasValue ? answer.specialRatesClaimed : undefined };
        setAnswer({ ...answer, ...changes });
        break;
      }
      case 'specialRatesClaimed': {
        const newSpecialRatesClaimed = { ...q, answer: value };
        validateQuestion(newSpecialRatesClaimed);
        setSpecialRatesClaimed(newSpecialRatesClaimed);

        const hasValue = value === 'Yes';
        const rateValidation = hasValue ? getWhdRateValidator() : undefined;
        const incomeTypeValidation = hasValue ? getRequiredValidator('questionnaire.treatyClaim.incomeType.errorMessage') : undefined;
        const articleValidation = hasValue ? getRequiredValidator('questionnaire.treatyClaim.articleParagraph.errorMessage') : undefined;
        const explanationValidation = hasValue ? getRequiredValidator('questionnaire.treatyClaim.explanation.errorMessage') : undefined;
        const answerChanges = value === 'No' ? { answer: undefined, isValid: undefined, errorMessages: undefined } : {};
        setWhdRate({ ...whdRate, ...rateValidation, ...answerChanges });
        setIncomeType({ ...incomeType, ...incomeTypeValidation, ...answerChanges });
        setArticleParagraph({ ...articleParagraph, ...articleValidation, ...answerChanges });
        setExplanation({ ...explanation, ...explanationValidation, ...answerChanges });
        setAnswer({ ...answer, specialRatesClaimed: value });
        break;
      }
      case 'whdRate': {
        const newWhdRate = { ...q, answer: value };
        validateQuestion(newWhdRate);
        setWhdRate(newWhdRate);
        setAnswer({ ...answer, whdRate: value });
        break;
      }
      case 'incomeType': {
        const newIncomeType = { ...q, answer: value };
        validateQuestion(newIncomeType);
        setIncomeType(newIncomeType);
        setAnswer({ ...answer, incomeType: value });
        break;
      }
      case 'articleParagraph': {
        const newArticleParagraph = { ...q, answer: value };
        validateQuestion(newArticleParagraph);
        setArticleParagraph(newArticleParagraph);
        setAnswer({ ...answer, articleParagraph: value });
        break;
      }
      case 'explanation': {
        const newExplanation = { ...q, answer: value };
        validateQuestion(newExplanation);
        setExplanation(newExplanation);
        setAnswer({ ...answer, explanation: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    dispatch(answerBlock(block.id, answer, isValid()));
  }, [answer]);

  useEffect(() => {
    if (claimed.answer !== block.answers?.claimed) {
      onQuestionChange(claimed, block.answers?.claimed);
    }
    if (country.answer !== block.answers?.country) {
      onQuestionChange(country, block.answers?.country);
    }
    if (specialRatesClaimed.answer !== block.answers?.specialRatesClaimed) {
      onQuestionChange(specialRatesClaimed, block.answers?.specialRatesClaimed);
    }
    if (whdRate.answer !== block.answers?.whdRate) {
      onQuestionChange(whdRate, block.answers?.whdRate);
    }
    if (incomeType.answer !== block.answers?.incomeType) {
      onQuestionChange(incomeType, block.answers?.incomeType);
    }
    if (articleParagraph.answer !== block.answers?.articleParagraph) {
      onQuestionChange(articleParagraph, block.answers?.articleParagraph);
    }
    if (explanation.answer !== block.answers?.explanation) {
      onQuestionChange(explanation, block.answers?.explanation);
    }
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (answer?.claimed === undefined) {
        onQuestionChange({ ...claimed, isForceEdited: true }, '');
      }
      if (answer?.claimed === 'Yes' && !answer?.country) {
        onQuestionChange({ ...country, isForceEdited: true }, '');
      }
      if (answer?.claimed === 'Yes' && answer?.country !== undefined && answer?.specialRatesClaimed === undefined) {
        onQuestionChange({ ...specialRatesClaimed, isForceEdited: true }, '');
      }
      if (answer?.specialRatesClaimed === 'Yes' && !answer?.whdRate) {
        onQuestionChange({ ...whdRate, isForceEdited: true }, '');
      }
      if (answer?.specialRatesClaimed === 'Yes' && !answer?.incomeType) {
        onQuestionChange({ ...incomeType, isForceEdited: true }, '');
      }
      if (answer?.specialRatesClaimed === 'Yes' && !answer?.articleParagraph) {
        onQuestionChange({ ...articleParagraph, isForceEdited: true }, '');
      }
      if (answer?.specialRatesClaimed === 'Yes' && !answer?.explanation) {
        onQuestionChange({ ...explanation, isForceEdited: true }, '');
      }
    }
  }, [block.isForceEdited]);

  return (<React.Fragment>
    <Question key={ claimed.id } blockId={ blockIdentifier } question={ claimed } { ...claimed.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    { country.visible(block.answers, {}, preQualAnswers) && <Question key={ country.id } blockId={ blockIdentifier } question={ country } { ...country.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    { specialRatesClaimed.visible(block.answers, {}, preQualAnswers) && <Question
      key={ specialRatesClaimed.id }
      blockId={ blockIdentifier }
      question={ specialRatesClaimed }
      { ...specialRatesClaimed.componentProps }
      onChange={ (q, a) => onQuestionChange(q, a) }
    /> }
    { whdRate.visible(block.answers, {}, preQualAnswers) && <Question key={ whdRate.id } blockId={ blockIdentifier } question={ whdRate } { ...whdRate.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    { incomeType.visible(block.answers, {}, preQualAnswers) && <Question key={ incomeType.id } blockId={ blockIdentifier } question={ incomeType } { ...incomeType.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    { articleParagraph.visible(block.answers, {}, preQualAnswers) && <Question
      key={ articleParagraph.id }
      blockId={ blockIdentifier }
      question={ articleParagraph }
      { ...articleParagraph.componentProps }
      onChange={ (q, a) => onQuestionChange(q, a) }
    /> }
    { explanation.visible(block.answers, {}, preQualAnswers) && <Question key={ explanation.id } blockId={ blockIdentifier } question={ explanation } { ...explanation.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
  </React.Fragment>);
};

QuestionTreatyClaimBlock.displayName = 'QuestionTreatyClaimBlock';

QuestionTreatyClaimBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  preQualAnswers: PropTypes.shape()
};

QuestionTreatyClaimBlock.defaultProps = {
  preQualAnswers: null
};

export default QuestionTreatyClaimBlock;
